import React, { useState, useEffect } from 'react';
import { XMarkIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import { DocumentSelector } from '../../common/DocumentSelector';
import { supabase } from '../../../../lib/supabase';
import { executeDataNode } from './dataNodeExecution';
import EditableTitle from '../../common/EditableTitle';
import { useProject } from '../../../../context/ProjectContext';
import { useToast } from '../../../../context/ToastContext';
import Modal from '../../common/Modal';

const CONDITION_OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equal To' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than', label: 'Less Than' },
  { value: 'contains', label: 'Contains' }
];

const RECORD_SELECTION_MODES = [
  { value: 'all', label: 'Select All Records' },
  { value: 'conditional', label: 'Select With Criteria' }
];

const MATCHING_MODES = [
  { value: 'all', label: 'Select All Matching Records' },
  { value: 'first', label: 'Select First Matching Record' }
];

export default function GetDataSettingsModal({ 
  isOpen, 
  onClose, 
  data, 
  onSave,
  nodeName 
}) {
  const { currentProject } = useProject();
  const [settings, setSettings] = useState({
    label: data.label || nodeName,
    selectedTable: data.selectedTable || null,
    selectionMode: data.selectionMode || 'all',
    selectedColumn: data.selectedColumn || null,
    matchingMode: data.matchingMode || 'all',
    conditions: data.conditions || []
  });

  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [testResults, setTestResults] = useState(null);
  const [isTesting, setIsTesting] = useState(false);
  const { addToast } = useToast();

  const filteredTables = tables.filter(table => 
    table.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (isOpen) {
      loadTables();
    }
  }, [isOpen, currentProject?.id]);

  const loadTables = async () => {
    if (!currentProject?.id) return;

    try {
      const { data: dataTables, error } = await supabase
        .from('data_tables')
        .select('*')
        .eq('project_id', currentProject.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTables(dataTables || []);
    } catch (error) {
      console.error('Error loading tables:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onSave({ ...data, ...settings });
    onClose();
  };

  const handleDelete = () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      setTimeout(() => setDeleteConfirm(false), 3000);
      return;
    }
    data.onDelete(data.id);
    onClose();
  };

  const handleAddTable = (table) => {
    setSettings(prev => ({
      ...prev,
      selectedTable: table
    }));
  };

  const handleTestQuery = async () => {
    setIsTesting(true);
    try {
      const result = await executeDataNode({ settings });
      console.log('Test query result:', result);
      
      if (result.success && result.output) {
        // Update the selected table with the latest column_order
        setSettings(prev => ({
          ...prev,
          selectedTable: {
            ...prev.selectedTable,
            column_order: result.output.column_order
          }
        }));
        // Store the raw dataset
        setTestResults(result.output.dataset);
      }
    } catch (error) {
      console.error('Error testing query:', error);
      addToast('Failed to test query', 'error');
    } finally {
      setIsTesting(false);
    }
  };

  // Helper function to format cell values
  const formatCellValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="w-[1200px] max-w-[90vw] max-h-[90vh] bg-dark-200 rounded-lg flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-dark-400">
          <div className="flex items-center justify-between">
            <EditableTitle 
              title={settings.label || nodeName || 'Get Data Table'} 
              onSave={(newTitle) => {
                setSettings(prev => ({
                  ...prev,
                  label: newTitle
                }));
              }}
              className="text-xl font-semibold text-gray-900 dark:text-white"
            />
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-300">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex">
          {/* Left Panel - Settings */}
          <div className="w-1/2 p-6 overflow-y-auto border-r border-dark-400">
            <div className="space-y-6">
              {/* Table Selection */}
              <div>
                <label className="block text-sm font-medium mb-4">Selected Table</label>
                {settings.selectedTable ? (
                  <div className="flex items-center justify-between p-3 bg-dark-300 border border-dark-400 rounded">
                    <div className="flex items-center gap-3">
                      <TableCellsIcon className="w-5 h-5 text-blue-400" />
                      <span>{settings.selectedTable.name}</span>
                    </div>
                    <button
                      onClick={() => setSettings(prev => ({ ...prev, selectedTable: null }))}
                      className="text-sm text-accent-primary hover:text-accent-primary/90"
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <div>
                    {/* Search Input */}
                    <input
                      type="text"
                      placeholder="Search tables..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-2 mb-4 bg-dark-300 border border-dark-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-primary"
                    />

                    {/* Table List */}
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {loading ? (
                        <div className="text-center py-4 text-gray-400">Loading...</div>
                      ) : filteredTables.length === 0 ? (
                        <div className="text-center py-4 text-gray-400">
                          No tables found
                        </div>
                      ) : (
                        filteredTables.map(table => (
                          <button
                            key={table.id}
                            onClick={() => handleAddTable(table)}
                            className="w-full flex items-center gap-3 p-3 rounded-lg transition-colors bg-dark-300 hover:bg-dark-400 border border-transparent"
                          >
                            <TableCellsIcon className="w-5 h-5 text-blue-400" />
                            <div className="flex-1 text-left">
                              <div className="font-medium">{table.name}</div>
                              <div className="text-sm text-gray-400">Data Table</div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Record Selection Mode */}
              <div>
                <label className="block text-sm font-medium mb-2">Record Selection</label>
                <select
                  value={settings.selectionMode}
                  onChange={(e) => setSettings(prev => ({ ...prev, selectionMode: e.target.value }))}
                  className="w-full px-3 py-2 bg-dark-200 border border-divider rounded focus:outline-none focus:border-accent-primary"
                >
                  {RECORD_SELECTION_MODES.map(mode => (
                    <option key={mode.value} value={mode.value}>{mode.label}</option>
                  ))}
                </select>
              </div>

              {/* Conditional Selection Settings */}
              {settings.selectionMode === 'conditional' && (
                <>
                  {/* Column Selection */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Conditions</label>
                    {settings.conditions.map((condition, index) => (
                      <div key={index} className="flex gap-2 mb-2">
                        {/* Field Selection */}
                        <select
                          value={condition.field || ''}
                          onChange={(e) => {
                            const newConditions = [...settings.conditions];
                            newConditions[index] = {
                              ...newConditions[index],
                              field: e.target.value
                            };
                            setSettings(prev => ({ ...prev, conditions: newConditions }));
                          }}
                          className="flex-1 px-3 py-2 bg-dark-200 border border-divider rounded focus:outline-none focus:border-accent-primary"
                        >
                          <option value="">Select field</option>
                          {settings.selectedTable?.column_order?.map(col => (
                            <option key={col.id} value={col.name}>{col.name}</option>
                          ))}
                        </select>

                        {/* Operator Selection */}
                        <select
                          value={condition.operator}
                          onChange={(e) => {
                            const newConditions = [...settings.conditions];
                            newConditions[index] = {
                              ...newConditions[index],
                              operator: e.target.value
                            };
                            setSettings(prev => ({ ...prev, conditions: newConditions }));
                          }}
                          className="flex-1 px-3 py-2 bg-dark-200 border border-divider rounded focus:outline-none focus:border-accent-primary"
                        >
                          {CONDITION_OPERATORS.map(op => (
                            <option key={op.value} value={op.value}>{op.label}</option>
                          ))}
                        </select>

                        {/* Value Input */}
                        <input
                          type="text"
                          value={condition.value || ''}
                          onChange={(e) => {
                            const newConditions = [...settings.conditions];
                            newConditions[index] = {
                              ...newConditions[index],
                              value: e.target.value
                            };
                            setSettings(prev => ({ ...prev, conditions: newConditions }));
                          }}
                          className="flex-1 px-3 py-2 bg-dark-200 border border-divider rounded focus:outline-none focus:border-accent-primary"
                          placeholder="Value"
                        />

                        {/* Delete Button */}
                        <button
                          onClick={() => {
                            const newConditions = settings.conditions.filter((_, i) => i !== index);
                            setSettings(prev => ({ ...prev, conditions: newConditions }));
                          }}
                          className="p-2 text-gray-400 hover:text-red-500"
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      </div>
                    ))}

                    {/* Add Condition Button */}
                    <button
                      onClick={() => {
                        setSettings(prev => ({
                          ...prev,
                          conditions: [...prev.conditions, { field: '', operator: 'equals', value: '' }]
                        }));
                      }}
                      className="mt-2 px-3 py-1 text-sm text-accent-primary hover:text-accent-primary/90"
                    >
                      + Add Condition
                    </button>
                  </div>

                  {/* Matching Mode - Moved to the end */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Matching Mode</label>
                    <select
                      value={settings.matchingMode}
                      onChange={(e) => setSettings(prev => ({ ...prev, matchingMode: e.target.value }))}
                      className="w-full px-3 py-2 bg-dark-200 border border-divider rounded focus:outline-none focus:border-accent-primary"
                    >
                      {MATCHING_MODES.map(mode => (
                        <option key={mode.value} value={mode.value}>{mode.label}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {/* Test Button */}
              <div>
                <button
                  onClick={handleTestQuery}
                  disabled={isTesting || !settings.selectedTable}
                  className="w-full px-4 py-2 bg-accent-primary text-white rounded hover:bg-accent-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isTesting ? 'Testing...' : 'Test Query'}
                </button>
              </div>
            </div>
          </div>

          {/* Right Panel - Test Results */}
          <div className="w-1/2 p-6 bg-dark-300 flex flex-col">
            <h3 className="text-sm font-medium mb-4">Test Results</h3>
            <div className="flex-1 border border-dark-400 rounded-lg overflow-hidden" 
                 style={{ maxHeight: 'calc(70vh - 100px)' }}>
              {testResults ? (
                <div className="h-full overflow-auto">
                  <table className="min-w-full divide-y divide-dark-400">
                    <thead className="bg-dark-400">
                      <tr>
                        {settings.selectedTable?.column_order?.map((column, index) => (
                          <th
                            key={typeof column === 'string' ? column : column.name}
                            className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                          >
                            {typeof column === 'string' ? column : column.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-dark-300 divide-y divide-dark-400">
                      {Array.isArray(testResults) && testResults.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {settings.selectedTable?.column_order?.map((column) => {
                            const columnName = typeof column === 'string' ? column : column.name;
                            return (
                              <td
                                key={columnName}
                                className="px-4 py-2 text-sm text-gray-300 whitespace-nowrap"
                              >
                                {formatCellValue(row[columnName])}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400">
                  {isTesting ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-accent-primary border-t-transparent"></div>
                      <span>Testing query...</span>
                    </div>
                  ) : (
                    <p>Click "Test Query" to see results</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-dark-400 p-4">
          <button
            type="button"
            onClick={handleDelete}
            className={`flex items-center gap-1 text-sm transition-colors ${
              deleteConfirm 
                ? 'bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded' 
                : 'text-gray-400 hover:text-red-500'
            }`}
          >
            <XMarkIcon className="w-3.5 h-3.5" />
            <span>{deleteConfirm ? 'Click to confirm delete' : 'Delete node'}</span>
          </button>
        </div>
      </div>
    </Modal>
  );
} 